import ApiMServer from "../services/ApiMServer";

const DEFAULT_CATALOG_PATH = "/charts/v1/getall";

export const getChartsCatalogPath = () =>
  process.env.REACT_APP_CHARTS_CATALOG_PATH || DEFAULT_CATALOG_PATH;

const TEST_CURVE = {
  id: "pp-elevador-test",
  nome: "PP ELEVADOR",
  curva:
    "['PP ELEVADOR,', 'PP ELEVADOR,', 'PP ELEVADOR,', 'PP ELEVADOR,', 'PP ELEVADOR,', 'PP ELEVADOR,']",
};

export const normalizeCurvasCatalog = (payload) => {
  if (!payload) {
    return null;
  }

  if (Array.isArray(payload)) {
    return payload[0] || null;
  }

  if (payload.chartComplement || payload.chartInmetro) {
    return payload;
  }

  if (payload.data) {
    return normalizeCurvasCatalog(payload.data);
  }

  if (payload.curvas) {
    return normalizeCurvasCatalog(payload.curvas);
  }

  return null;
};

export const buildBrazilianCurveOptions = (catalogRoot) => {
  if (!catalogRoot) {
    return [];
  }

  const allRegions = Object.values(catalogRoot);
  let allCurves = [];
  allRegions.forEach((region) => {
    if (Array.isArray(region)) {
      allCurves = [...allCurves, ...region];
    }
  });

  const filtered = allCurves
    .filter((item) => {
      const country = item.pais;
      return Array.isArray(country) ? country.includes("BRA") : country === "BRA";
    })
    .map((item, index) => {
      const rawName = (item.nome && item.nome[0]) || "Sem nome";
      const displayName = rawName.replace(/,$/, "");
      const legacyValue =
        item.nome && item.nome.length > 1
          ? `['${item.nome.join("', '")}']`
          : rawName;

      return {
        id: item._id || `curve-${index}`,
        nome: displayName,
        curva: legacyValue,
      };
    });

  return [...filtered, TEST_CURVE].sort((a, b) => a.nome.localeCompare(b.nome));
};

export async function loadStaticCurvasCatalog() {
  const module = await import("./Charts");
  return normalizeCurvasCatalog(module.curvas);
}

export async function fetchCurvasCatalogFromApi() {
  if (!process.env.REACT_APP_API_MOTOMCO) {
    throw new Error("REACT_APP_API_MOTOMCO não configurada");
  }

  const { data } = await ApiMServer.get(getChartsCatalogPath());
  const catalogRoot = normalizeCurvasCatalog(data);

  if (!catalogRoot) {
    throw new Error("Resposta da API de curvas em formato inválido");
  }

  return catalogRoot;
}

export const MAX_CHARTS_API_ATTEMPTS = 10;

let apiSuccessCache = null;
let fallbackOptionsCache = null;
let lastDisplayResult = null;
let apiAttemptCount = 0;
let inflightPromise = null;

const buildApiResult = (catalogRoot) => ({
  options: buildBrazilianCurveOptions(catalogRoot),
  source: "api",
  error: null,
  apiAttempts: apiAttemptCount,
  attemptsExhausted: false,
});

async function buildFallbackResult(apiError) {
  const exhausted = apiAttemptCount >= MAX_CHARTS_API_ATTEMPTS;
  const baseMessage = apiError?.message || "API de curvas indisponível";

  try {
    if (!fallbackOptionsCache) {
      const catalogRoot = await loadStaticCurvasCatalog();
      fallbackOptionsCache = buildBrazilianCurveOptions(catalogRoot);
    }

    return {
      options: fallbackOptionsCache,
      source: "fallback",
      error: exhausted
        ? `Catálogo local (API indisponível após ${MAX_CHARTS_API_ATTEMPTS} tentativas).`
        : baseMessage,
      apiAttempts: apiAttemptCount,
      attemptsExhausted: exhausted,
    };
  } catch (fallbackError) {
    return {
      options: [],
      source: "fallback",
      error: fallbackError?.message || "Não foi possível carregar curvas",
      apiAttempts: apiAttemptCount,
      attemptsExhausted: exhausted,
    };
  }
}

function rememberDisplayResult(result) {
  lastDisplayResult = result;
  return result;
}

async function executeApiAttempt() {
  if (apiSuccessCache) {
    return apiSuccessCache;
  }

  if (apiAttemptCount >= MAX_CHARTS_API_ATTEMPTS) {
    return rememberDisplayResult(
      await buildFallbackResult(new Error("Limite de tentativas atingido"))
    );
  }

  if (inflightPromise) {
    return inflightPromise;
  }

  inflightPromise = (async () => {
    apiAttemptCount += 1;
    try {
      const catalogRoot = await fetchCurvasCatalogFromApi();
      apiSuccessCache = buildApiResult(catalogRoot);
      lastDisplayResult = apiSuccessCache;
      return apiSuccessCache;
    } catch (apiError) {
      console.warn(
        `Falha ao carregar curvas no mserver (tentativa ${apiAttemptCount}/${MAX_CHARTS_API_ATTEMPTS}); usando catálogo local.`,
        apiError
      );
      return rememberDisplayResult(await buildFallbackResult(apiError));
    } finally {
      inflightPromise = null;
    }
  })();

  return inflightPromise;
}

export function getCachedBrazilianCurves() {
  return apiSuccessCache || lastDisplayResult;
}

export function getChartsApiAttemptCount() {
  return apiAttemptCount;
}

export function clearBrazilianCurvesCache() {
  apiSuccessCache = null;
  fallbackOptionsCache = null;
  lastDisplayResult = null;
  apiAttemptCount = 0;
  inflightPromise = null;
}

export async function loadBrazilianCurvesCatalog({ forceRefresh = false } = {}) {
  if (forceRefresh) {
    clearBrazilianCurvesCache();
  }

  if (apiSuccessCache) {
    return apiSuccessCache;
  }

  return executeApiAttempt();
}

/** Nova tentativa na API ao trocar de equipamento (até MAX_CHARTS_API_ATTEMPTS). */
export async function retryBrazilianCurvesCatalogOnNavigation() {
  if (apiSuccessCache) {
    return apiSuccessCache;
  }

  if (apiAttemptCount >= MAX_CHARTS_API_ATTEMPTS) {
    return rememberDisplayResult(
      lastDisplayResult ||
        (await buildFallbackResult(new Error("Limite de tentativas atingido")))
    );
  }

  return executeApiAttempt();
}
